<script setup lang="ts">
import AppButton from '@/components/base/AppButton.vue';
import AppSelectField from '@/components/base/AppSelectField.vue';
import AppTextArea from '@/components/base/AppTextArea.vue';
import AppTextField from '@/components/base/AppTextField.vue';
import AppTypography from '@/components/base/AppTypography.vue';
import BrowseHeader from '@/components/browse/header/BrowseHeader.vue';
import { useSupportOptions } from '@/composables/support/useSupportOptions';
import { SUPPORT_MESSAGE_MAX_LENGTH, useSupportRequest } from '@/composables/support/useSupportRequest';

definePageMeta({
  layout: 'account',
  auth: false,
});

useHead({
  title: 'Contact Support',
});

const SUPPORT_GROUP = 'tradel_platform';

const {
  group,
  reasonItems,
  pending: loadingOptions,
  error: optionsError,
  refresh: reloadOptions,
} = await useSupportOptions(SUPPORT_GROUP);

const { form, v$, errors, pending, ticket, isAuthenticated, needsContactDetails, requiresOrder, submit, reset } =
  useSupportRequest({ groupKey: SUPPORT_GROUP, group });

const unavailable = computed(() => Boolean(optionsError.value) || (!loadingOptions.value && !group.value));
</script>

<template>
  <v-container fluid class="account-container">
    <BrowseHeader class="account-header">
      <template #title>
        <AppTypography class="support-title" type="headline-1">Contact Support</AppTypography>
      </template>
    </BrowseHeader>

    <div class="account-content support">
      <div class="support__header">
        <AppTypography type="title-1">{{ group?.label ?? 'Contact Support' }}</AppTypography>
        <AppTypography v-if="group?.description" type="body-2" class="support__description">
          {{ group.description }}
        </AppTypography>
      </div>

      <div v-if="loadingOptions" class="support__state">
        <v-progress-circular indeterminate color="primary" />
      </div>

      <div v-else-if="unavailable" class="support__state">
        <AppTypography type="body-1" class="mb-4">
          We couldn't load the support form right now. Please try again.
        </AppTypography>
        <AppButton variant="outlined" @click="reloadOptions()">Retry</AppButton>
      </div>

      <!-- Confirmation: the ticket number is the only record the customer gets. -->
      <div v-else-if="ticket" class="support__state support__confirmation">
        <v-icon icon="mdi-check-circle-outline" color="success" size="48" class="mb-4" />
        <AppTypography type="title-2" class="mb-2">Your request has been received</AppTypography>
        <AppTypography type="body-1" class="mb-1">
          Reference <strong>{{ ticket.ticket_number }}</strong>
        </AppTypography>
        <AppTypography type="body-2" class="mb-6 support__description">
          {{ ticket.support_group_label }} &middot; {{ ticket.reason_label }} — our team will be in touch by email.
        </AppTypography>
        <AppButton variant="outlined" @click="reset">Send another request</AppButton>
      </div>

      <form v-else class="support__form" @submit.prevent="submit">
        <div class="support__fields">
          <AppSelectField
            v-model="form.reason"
            label="What do you need help with?"
            :items="reasonItems"
            :disabled="pending"
            :error-messages="errors.reason"
          />

          <template v-if="requiresOrder">
            <AppTextField
              v-model="form.order_id"
              label="Order number (optional)"
              :disabled="pending"
              :error-messages="errors.order_id"
            />
            <AppTypography v-if="!isAuthenticated" type="caption-1" class="support__hint support__hint--tight">
              Sign in to attach this request to your order, or leave the number here and we'll include it in your
              message.
            </AppTypography>
          </template>

          <template v-if="needsContactDetails">
            <AppTextField
              v-model="form.name"
              label="Your name"
              :disabled="pending"
              :error-messages="errors.name"
            />
            <AppTextField
              v-model="form.email"
              label="Email address"
              type="email"
              :disabled="pending"
              :error-messages="errors.email"
            />
          </template>

          <AppTextField
            v-model="form.phone"
            label="Phone number (optional)"
            :disabled="pending"
            :error-messages="errors.phone"
          />

          <AppTextArea
            v-model="form.message"
            label="How can we help?"
            placeholder="Tell us what happened, and anything we should know to look into it."
            with-counter
            :max-length="SUPPORT_MESSAGE_MAX_LENGTH"
            :disabled="pending"
            :error-messages="errors.message"
            class="bordered-input"
          />
        </div>

        <div class="support__action">
          <AppButton type="submit" :disabled="v$.$invalid || pending" :loading="pending" block>Send request</AppButton>
        </div>
      </form>
    </div>
  </v-container>
</template>

<style lang="scss" scoped>
.account-container {
  .support-title {
    display: none;
  }

  .support {
    max-width: 480px;

    &__header {
      padding-top: 12px;
      padding-bottom: 16px;
    }

    &__description {
      color: rgb(var(--v-theme-on-surface-variant));
      margin-top: 4px;
    }

    &__fields {
      display: flex;
      flex-direction: column;
      gap: 20px;
      margin-bottom: 32px;
    }

    &__hint {
      color: rgb(var(--v-theme-on-surface-variant));

      &--tight {
        margin-top: -12px;
      }
    }

    &__state {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
      padding: 48px 0;
    }
  }

  @media (max-width: 768px) {
    .support {
      margin-top: 20px;

      &__header {
        display: none;
      }

      &__form {
        display: flex;
        flex-grow: 1;
        flex-direction: column;
        justify-content: space-between;
      }

      &__action {
        position: fixed;
        bottom: 0;
        left: 0;
        width: 100%;
        padding: 16px;

        button {
          max-width: 430px;
          min-width: unset;
          width: 100%;
          margin: auto;
        }
      }
    }

    .support-title {
      display: block;
    }
  }
}
</style>
